import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { API } from "aws-amplify";
import { useButtonLocationDialog } from "hooks/useButtonLocationDialog";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { usePractice } from "hooks/usePractice";
import { useRecipient } from "hooks/useRecipient";
import { useRegions } from "hooks/useRegions";
import { CRITICAL_BATTERY_LEVEL } from "models/constants";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { DialogWindow } from "UI/DialogWindow";
import { SenderSelector } from "UI/SenderSelector";
import { SpecialistSelector } from "UI/SpecialistSelector";
import { convertDeviceType, getSpecialistReducer } from "utils/helpers";
import * as SC from "./EditButtonLocationDialog.style";
import { ModalSpinner } from "./ModalSpinner";

export const EditButtonLocationDialog = ({
  buttonId,
  handleClose,
  refreshList,
  practiceId = null,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning, openConfirmationWithCallback } =
    useContext(GlobalContext);
  const { regionsOptions } = useRegions();

  const {
    admins,
    setAdmins,
    marketers,
    setMarketers,
    schedulers,
    setSchedulers,
    handleChangeSpecialist,
    isUpdateButtonDisabled,
    selectedSenders,
    setSelectedSenders,
    selectedProviders,
    setSelectedProviders,
    selectedSpecialists,
    setSelectedSpecialists,
  } = useButtonLocationDialog();

  const [buttonLocation, setButtonLocation] = useState(null);
  const [hardButton, setHardButton] = useState(undefined);
  const [isReadyForEditing, setIsReadyForEditing] = useState(false);
  const [isButtonLocationLoading, setIsButtonLocationLoading] = useState(true);

  const { practice, providers, isPracticeLoading } = usePractice({
    practiceId: practiceId ?? buttonLocation?.organizationId,
  });

  const { specialists, isRecipientLoading } = useRecipient({
    recipientId: buttonLocation?.recipientId,
  });

  const getSelectedIdsByRole = (role) => {
    return specialists
      .filter((specialist) => specialist?.role === role)
      .filter(({ id }) => selectedSpecialists.includes(id))
      .map(({ id }) => id);
  };

  const handleReplaceBattery = createApiCall(async () => {
    const result = await API.post("restApi", "/replace_battery", {
      body: {
        deviceId: buttonId,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    setHardButton(result?.hardButton);

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Success
          </Typography>
        </Stack>
      ),
      content: <>Battery replacement information updated successfully.</>,
    });
  });

  const handleReplace = () =>
    openConfirmationWithCallback({
      title: "Please confirm",
      message: "Have you replaced the battery in this button device recently?",
      callbackFn: () => {
        handleReplaceBattery();
      },
      buttonLabels: ["No", "Yes"],
    });

  const deleteButtonLocation = createApiCall(async () => {
    const result = await API.del(
      "restApi",
      `/button_locations/${buttonId}`,
      {}
    );

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Success
          </Typography>
        </Stack>
      ),
      content: <>Button deleted successfully</>,
    });

    refreshList();
    handleClose();
  });

  const handleCancel = () => {
    if (isUpdateButtonDisabled) {
      handleClose();
      return;
    }

    openConfirmationWithCallback({
      title: "Please confirm",
      message: "All changes will be lost. Proceed?",
      callbackFn: () => {
        handleClose();
      },
      buttonLabels: ["No", "Yes"],
    });
  };

  const handleResendInstallLink = createApiCall(async () => {
    const result = await API.post("restApi", "/resend_install_link", {
      body: {
        deviceId: buttonId,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Success
          </Typography>
        </Stack>
      ),
      content: <>Install link resent successfully</>,
    });
  });

  const handleDelete = () =>
    openConfirmationWithCallback({
      title: "Please confirm",
      message: "Do you really want to delete this button?",
      callbackFn: () => {
        deleteButtonLocation();
      },
      buttonLabels: ["No", "Yes"],
    });

  const handleSave = createApiCall(async () => {
    const newData = {
      ...buttonLocation,
      admins: getSelectedIdsByRole("Admin"),
      marketers: getSelectedIdsByRole("Marketer"),
      schedulers: getSelectedIdsByRole("Scheduler"),
      senders: Object.keys(selectedSenders)
        .filter((key) => selectedSenders[key].selected)
        .map((key) => key),
      providers: Object.keys(selectedProviders)
        .filter((key) => selectedProviders[key].selected)
        .map((key) => key),
    };

    const result = await API.post("restApi", "/button_locations", {
      body: {
        buttonLocation: newData,
      },
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      return;
    }

    showWarning({
      title: (
        <Stack justifyContent={"center"} alignItems={"center"} gap={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Success
          </Typography>
        </Stack>
      ),
      content: <>Button updated successfully</>,
    });

    refreshList();
    handleClose();
  });

  useEffect(() => {
    if (specialists.length === 0 || !regionsOptions) {
      return;
    }

    setAdmins(
      specialists
        .filter(({ role }) => role === "Admin")
        .reduce(getSpecialistReducer(regionsOptions), {})
    );

    setMarketers(
      specialists
        .filter(({ role }) => role === "Marketer")
        .reduce(getSpecialistReducer(regionsOptions), {})
    );
    setSchedulers(
      specialists
        .filter(({ role }) => role === "Scheduler")
        .reduce(getSpecialistReducer(regionsOptions), {})
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialists, regionsOptions]);

  useEffect(() => {
    if (!buttonId) return;

    const loadData = createApiCall(async () => {
      setIsButtonLocationLoading(true);
      try {
        const result = await API.get(
          "restApi",
          `/button_locations/${buttonId}`,
          {}
        );

        const buttonLocation = result?.Item ?? {};

        const selectedSpecialistIds = (result?.specialists ?? []).map(
          ({ specialistId }) => specialistId
        );
        setSelectedSpecialists(selectedSpecialistIds);
        setButtonLocation(buttonLocation);
        setHardButton(result?.hardButton);

        const providers = result?.providers;
        const selectedProviders = result?.selectedProviders;
        const providerList = providers.reduce(
          (acc, { id, name, speciality }) => ({
            ...acc,
            [id]: {
              label: [name, speciality].filter(Boolean).join(", "),
              selected: selectedProviders?.includes(id),
            },
          }),
          {}
        );
        setSelectedProviders(providerList);

        const senders = result?.senders ?? [];
        const selectedSenders = result?.selectedSenders;
        const senderList = senders.reduce(
          (acc, { id, firstName, lastName, jobTitle, email, phone }) => ({
            ...acc,
            [id]: {
              label: [`${firstName} ${lastName}`, jobTitle, phone, email]
                .filter(Boolean)
                .join(", "),
              selected: selectedSenders?.includes(id),
            },
          }),
          {}
        );
        setSelectedSenders(senderList);
        setIsReadyForEditing(true);
      } catch (error) {
        console.error(error);
        dispatch(showError(error));
      } finally {
        setIsButtonLocationLoading(false);
      }
    });

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonId]);

  const { deviceType } = buttonLocation ?? {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileButtonSx = { padding: "10px 10px" };
  const sx = isMobile ? mobileButtonSx : {};

  const isLoading =
    isButtonLocationLoading || isPracticeLoading || isRecipientLoading;

    const selectedProviderIds = Object.keys(selectedProviders).filter((id) => selectedProviders[id]?.selected);
    const assignedProviders = providers?.filter(({ id }) => selectedProviderIds.includes(String(id)));

  return (
    <DialogWindow
      open
      withCloseButton
      handleClose={handleCancel}
      title={"Edit Button"}
      content={
        <Stack mt={3} gap={2}>
          <SC.Info gap={2}>
            <SC.Property
              justifyContent={"space-between"}
              flexWrap={"wrap"}
              alignItems={"center"}
            >
              <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
                <SC.Label>Button ID:</SC.Label>{" "}
                {isMobile ? buttonId.slice(0, 8) + "..." : buttonId}
              </Stack>
              {!!hardButton?.name && (
                <Stack direction={"row"} gap={1}>
                  <SC.Label>Button Name:</SC.Label> {hardButton?.name}
                </Stack>
              )}
              <Stack direction={"row"} gap={1}>
                <SC.Label>Type:</SC.Label> {convertDeviceType(deviceType)}
              </Stack>
              {deviceType !== "Virtual" && (
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  gap={1}
                  flexWrap={"wrap"}
                >
                  <Stack direction={"row"} gap={1}>
                    <SC.Label>Battery:</SC.Label>{" "}
                    {hardButton?.lastBatteryLevel >= 0 ? (
                      <div
                        className={
                          hardButton.lastBatteryLevel <= CRITICAL_BATTERY_LEVEL
                            ? "text-red-700"
                            : ""
                        }
                      >
                        {hardButton.lastBatteryLevel}%
                      </div>
                    ) : (
                      "N/A"
                    )}
                  </Stack>
                  {hardButton?.manufacturer === "SORACOM" && (
                    <Button
                      variant="outlined"
                      sx={{ padding: "2px 8px" }}
                      onClick={handleReplace}
                    >
                      Replace Battery
                    </Button>
                  )}
                </Stack>
              )}
            </SC.Property>
          </SC.Info>
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
            alignItems={"stretch"}
            gap={2}
          >
            <Stack
              gap={2}
              alignItems={"stretch"}
              justifyContent={"space-between"}
              flex={1}
            >
              <SC.Title alignSelf={"flex-start"}>Practice</SC.Title>
              <SC.Info flex={1}>
                <Stack
                  direction={"row"}
                  flexWrap={"wrap"}
                  columnGap={2}
                  rowGap={0.5}
                >
                  <SC.Property>
                    <SC.Label>Name:</SC.Label>
                    {practice?.name}
                  </SC.Property>
                  <SC.Property>
                    <SC.Label>Speciality:</SC.Label>
                    {practice?.speciality}
                  </SC.Property>
                  <SC.Property>
                    <SC.Label>Phone:</SC.Label>
                    <a href={`tel:${practice?.phone}`}>{practice?.phone}</a>
                  </SC.Property>
                  <SC.Property>
                    <SC.Label>Address:</SC.Label>
                    {practice?.street &&
                      `${practice?.street}, ${practice?.city}, ${practice?.state}, ${practice?.zipCode}`}
                  </SC.Property>
                  {practice?.email && (
                    <SC.Property>
                      <SC.Label>Email:</SC.Label>
                      <a
                        href={`mailto:${practice?.email}`}
                        className="break-all"
                      >
                        {practice?.email}
                      </a>
                    </SC.Property>
                  )}
                  {!!practice?.website && (
                    <SC.Property>
                      <SC.Label>Website:</SC.Label>
                      <a href={practice?.website} className="break-all">
                        {practice?.website}
                      </a>
                    </SC.Property>
                  )}
                  {!!practice?.marketingRegionId && (
                    <SC.Property>
                      <SC.Label>Region:</SC.Label>
                      {regionsOptions[practice?.marketingRegionId] ?? ""}
                    </SC.Property>
                  )}
                </Stack>
              </SC.Info>
            </Stack>
            <Stack
              gap={2}
              alignItems={"stretch"}
              justifyContent={"space-between"}
              flex={1}
            >
              <SC.Title alignSelf={"flex-start"}>Provider</SC.Title>
              <SC.Info flex={1}>
                {assignedProviders?.map(({ id, name, speciality }) => (
                  <Stack
                    direction={"row"}
                    flexWrap={"wrap"}
                    gap={"10px"}
                    key={id}
                  >
                    <SC.Property>
                      <SC.Label>Name:</SC.Label> {name}
                    </SC.Property>
                    <SC.Property>
                      <SC.Label>Speciality:</SC.Label> {speciality}
                    </SC.Property>
                  </Stack>
                ))}
              </SC.Info>
            </Stack>
            <Stack
              gap={2}
              alignItems={"stretch"}
              justifyContent={"space-between"}
              flex={1}
            >
              <SC.Title alignSelf={"flex-start"}>Senders</SC.Title>
              <SC.Info flex={1}>
                <SenderSelector
                  specialists={selectedSenders}
                  role={"Originator"}
                  handleChangeSpecialist={handleChangeSpecialist}
                  isLoading={isLoading}
                  noHeader
                />
              </SC.Info>
            </Stack>
          </Stack>
          {isReadyForEditing && (
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent={"space-between"}
              alignItems={"flex-start"}
              gap={4}
            >
              <Box flex={1}>
                <SpecialistSelector
                  specialists={admins}
                  selectedSpecialistIds={selectedSpecialists}
                  role={"Admin"}
                  handleChangeSpecialist={handleChangeSpecialist}
                />
              </Box>
              {Object.keys(marketers).length > 0 && (
                <Box flex={1}>
                  <SpecialistSelector
                    specialists={marketers}
                    selectedSpecialistIds={selectedSpecialists}
                    role={"Marketer"}
                    handleChangeSpecialist={handleChangeSpecialist}
                  />
                </Box>
              )}
              {Object.keys(schedulers).length > 0 && (
                <Box flex={1}>
                  <SpecialistSelector
                    specialists={schedulers}
                    selectedSpecialistIds={selectedSpecialists}
                    role={"Scheduler"}
                    handleChangeSpecialist={handleChangeSpecialist}
                  />
                </Box>
              )}
            </Stack>
          )}
          <ModalSpinner open={!isReadyForEditing} />
        </Stack>
      }
      actions={
        <Stack
          direction={"row"}
          justifyContent={"center"}
          alignItems={"stretch"}
          width={"100%"}
          gap={1}
          mb={2}
          mt={2}
          pl={3}
          pr={3}
        >
          {/* <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button> */}
          <Button
            variant="contained"
            onClick={handleDelete}
            color="warning"
            sx={sx}
          >
            Delete
          </Button>
          <Button
            variant="outlined"
            onClick={handleResendInstallLink}
            disabled={!["Virtual", "Soft", "Digital"].includes(deviceType)}
            sx={sx}
          >
            Resend install link
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isUpdateButtonDisabled}
            sx={sx}
          >
            Save Changes
          </Button>
        </Stack>
      }
    />
  );
};
